/**
 * 🎬 demoScenes.config.js — Source unique des 14 scenes de la demo CoachDIA
 * =========================================================================
 *
 * v7 (12/02/2026 — Iter351) — Bascule script V2 PREMIUM (validation produit)
 *   - 14 scenes (vs 13) : ajout scene "entrainement_libre" en position 4 (pivot)
 *   - Voiceovers refondus : phrases courtes, attaques marquees, densite +50%,
 *     zero connecteur administratif, Owen incarne des la scene 02
 *   - Voix Owen V4.5 unifiee (TTS_INSTRUCTION_DEMO_CHALLENGER herite de
 *     _TTS_INSTRUCTION_BASE) — UNE SEULE VOIX OWEN sur toute la plateforme
 *   - CTA final renforce (urgence concurrentielle)
 *
 * Chaque scene contient :
 *   - id, chapter, kicker, title, description, type, Frame, voiceover,
 *     subtitle, durationMs, audioPeak, cta (final uniquement)
 *
 * REGLE ABSOLUE : `voiceover` doit etre STRICTEMENT IDENTIQUE a
 * `DEMO_VOICEOVERS` dans `app/routes/demo_voiceover.py`.
 */

import {
  SceneOverview,
  SceneOwen,
  SceneCertifiant,
  SceneHumain,
  SceneRapport,
  ScenePrism,
} from '../components/HeroDemoVideo';

import {
  SceneProblem,
  SceneTheory,
  SceneStructure,
  SceneSequential,
  ScenePulse,
  ScenePulseRoom,
  SceneCertificate,
  SceneTransformation,
  SceneEntrainementLibre,
} from '../components/demo/DemoExtraScenes';

export const DEMO_SCENES = [
  {
    id: 'problem',
    chapter: 1,
    kicker: 'Scene 01 · Le constat',
    title: 'Vous formez vos managers. Mais personne ne sait vraiment comment ils décident.',
    description: 'Hook choc : sans observation, pas de progression mesurable.',
    type: 'problem',
    Frame: SceneProblem,
    voiceover:
      "Aujourd'hui, la plupart des entreprises forment leurs managers… mais très peu savent comment ils décident réellement sous pression. Aucune preuve. Aucune visibilité. Aucune continuité. La formation s'arrête souvent à la théorie. Mais le leadership, lui, se joue tous les jours. Dans les arbitrages. Dans les conflits. Dans les décisions difficiles.",
    subtitle: "Le leadership réel se révèle sous tension. Pas dans les slides.",
    durationMs: 20600,
    audioPeak: 0.06,
  },
  {
    id: 'platform',
    chapter: 2,
    kicker: 'Scene 02 · Entree CoachDIA',
    title: 'CoachDia ne vend pas des cours. CoachDia transforme les décisions en preuves.',
    description: 'Vue plateforme : 5 piliers, 20 modules, Owen nomme.',
    type: 'platform',
    Frame: SceneOverview,
    voiceover:
      "CoachDia n'est pas une énième plateforme. C'est un système complet de transformation managériale. Cinq piliers. Vingt modules. Une progression structurée. Et au centre : Owen. Un coach IA exigeant, conçu pour pousser les managers à réfléchir, décider, assumer et progresser.",
    subtitle: '5 piliers · 20 modules · Owen, le coach IA exécutif.',
    durationMs: 18400,
    audioPeak: 0.08,
  },
  {
    id: 'theory',
    chapter: 3,
    kicker: 'Scene 03 · Theorie',
    title: 'Comprendre prend quelques minutes. La transformation se joue dans l\'action.',
    description: 'Theorie courte, dense, posee par Owen.',
    type: 'theory',
    Frame: SceneTheory,
    voiceover:
      "Chaque module commence par une théorie courte, dense et structurée. Pas de cours interminables. Pas de surcharge cognitive. Seulement l'essentiel : les distinctions qui changent réellement la pratique du management. Puis très vite… vous passez à l'action.",
    subtitle: 'Une théorie courte, dense et immédiatement applicable.',
    durationMs: 18400,
    audioPeak: 0.07,
  },
  {
    id: 'entrainement_libre',
    chapter: 4,
    kicker: 'Scene 04 · Entrainement libre',
    title: 'Ici, vous pouvez tester, hésiter, recommencer.',
    description: 'Pont strategique entre theorie et certifiant. Owen joue le role, zero pression.',
    type: 'training',
    Frame: SceneEntrainementLibre,
    voiceover:
      "Avant l'évaluation certifiante, vous entrez dans un espace d'entraînement. Owen prend un rôle. Vous confronte à des situations réelles. Vous pousse à réfléchir. Vous pouvez essayer. Vous tromper. Recommencer. Sans pression. Sans note. Mais jamais sans profondeur.",
    subtitle: 'L\'entraînement construit la compétence avant l\'évaluation.',
    durationMs: 17800,
    audioPeak: 0.06,
  },
  {
    id: 'owen',
    chapter: 5,
    kicker: 'Scene 05 · Owen certifiant',
    title: 'Owen ne cherche pas à valider rapidement. Il cherche ce qui tient réellement.',
    description: "Coach IA qui ne lache rien — decision qui tient face au reel.",
    type: 'ai',
    Frame: SceneOwen,
    voiceover:
      "Puis vient l'évaluation. Et Owen change de posture. Il ne cherche plus à aider rapidement. Il cherche à comprendre si votre décision tient réellement face au réel. Il challenge. Il relance. Il creuse. Jusqu'à obtenir quelque chose de solide. Quelque chose d'assumé. Quelque chose d'applicable.",
    subtitle: 'Questions difficiles · tensions réelles · refus des réponses faciles.',
    durationMs: 18900,
    audioPeak: 0.06,
  },
  {
    id: 'structure',
    chapter: 6,
    kicker: 'Scene 06 · Structure pedagogique',
    title: 'Une compétence n\'existe que lorsqu\'elle est démontrée.',
    description: '2 objectifs · 5 criteres · validation uniquement sur preuves tenues.',
    type: 'structure',
    Frame: SceneStructure,
    voiceover:
      "Chaque module possède deux objectifs de transformation. Et chaque objectif repose sur cinq critères précis à démontrer. La validation ne repose jamais sur une impression. Ni sur une intention. Seulement sur des preuves observables et démontrées.",
    subtitle: '2 objectifs · 5 critères · validation uniquement sur preuves.',
    durationMs: 16600,
    audioPeak: 0.07,
  },
  {
    id: 'sequential',
    chapter: 7,
    kicker: 'Scene 07 · Processus sequentiel',
    title: 'Aucun raccourci. Aucune validation artificielle.',
    description: 'Owen ne fait aucun cadeau · l\'intransigeance fait la competence.',
    type: 'sequential',
    Frame: SceneSequential,
    voiceover:
      "Dans CoachDia, une étape ne s'ouvre pas automatiquement. Chaque progression doit être démontrée. Owen refuse les réponses récitées. Les formulations superficielles. Les validations faciles. Parce qu'une vraie compétence se construit dans l'exigence.",
    subtitle: 'La progression se mérite étape après étape.',
    durationMs: 16800,
    audioPeak: 0.08,
  },
  {
    id: 'prism',
    chapter: 8,
    kicker: 'Scene 08 · Prism individuel',
    title: 'Prism révèle votre manière de diriger.',
    description: 'Diagnostic individuel · plan 30/60/90 · concret, trackable, tenu.',
    type: 'prism',
    Frame: ScenePrism,
    voiceover:
      "Prism analyse votre signature de leadership. Vos forces naturelles. Vos tensions. Vos zones de vigilance. Puis construit une trajectoire concrète de progression : 30 jours. 60 jours. 90 jours. Ce n'est pas un simple profil. C'est un véritable outil de pilotage personnel.",
    subtitle: 'Forces · tensions · angles morts · trajectoire de progression.',
    durationMs: 17400,
    audioPeak: 0.09,
  },
  {
    id: 'pulse',
    chapter: 9,
    kicker: 'Scene 09 · Pulse equipe',
    title: 'Pulse lit la dynamique réelle de vos équipes.',
    description: 'Donnees anonymisees · 5 indicateurs · actionnables maintenant.',
    type: 'pulse',
    Frame: ScenePulse,
    voiceover:
      "Pulse permet de comprendre ce que vit réellement une équipe. Pas à travers des intuitions. Mais à travers des signaux structurés et anonymisés. Engagement. Clarté. Tension. Confiance. Alignement. Des informations immédiatement actionnables par les managers et les RH.",
    subtitle: 'Des signaux clairs pour agir avant les blocages.',
    durationMs: 16500,
    audioPeak: 0.10,
  },
  {
    id: 'pulse_room',
    chapter: 10,
    kicker: 'Scene 10 · Salle Pulse',
    title: 'Une vision stratégique du leadership collectif.',
    description: 'Salle de presentation Pulse pour la direction et les RH.',
    type: 'pulse_room',
    Frame: ScenePulseRoom,
    voiceover:
      "La Salle Pulse donne aux directions et aux RH une lecture globale du leadership collectif. Les forces. Les fragilités. Les zones de tension. Les risques humains. Enfin, le leadership devient pilotable avec précision.",
    subtitle: 'Direction · RH · pilotage exécutif.',
    durationMs: 14300,
    audioPeak: 0.10,
  },
  {
    id: 'human',
    chapter: 11,
    kicker: 'Scene 11 · Coaching humain',
    title: "L'humain reprend là où l'IA ouvre la profondeur.",
    description: 'Coach humain visio · continuite IA → humain sur consentement.',
    type: 'human',
    Frame: SceneHumain,
    voiceover:
      "Quand un accompagnement humain devient pertinent, un coach certifié peut reprendre le relais. Avec votre accord, il accède à votre contexte, vos progrès, vos tensions, vos points de vigilance. La continuité est totale. Vous ne recommencez jamais de zéro.",
    subtitle: 'Coach certifié · continuité · contexte partagé avec consentement.',
    durationMs: 17500,
    audioPeak: 0.08,
  },
  {
    id: 'report',
    chapter: 12,
    kicker: 'Scene 12 · Rapport IA',
    title: "Chaque session devient un levier concret de progression.",
    description: 'Synthese IA confidentielle · une seule prochaine action.',
    type: 'report',
    Frame: SceneRapport,
    voiceover:
      "Après chaque session, CoachDia génère un rapport confidentiel conçu pour faire avancer la réflexion. Une lecture claire de l'échange. Les points de progression identifiés. Les tensions observées. Les angles de vigilance. Et la prochaine action réellement utile. L'utilisateur avance. Le coach comprend plus vite. Et chaque session construit une continuité réelle.",
    subtitle: 'Analyse · tensions · prochaine action utile.',
    durationMs: 23300,
    audioPeak: 0.08,
  },
  {
    id: 'certificate',
    chapter: 13,
    kicker: 'Scene 13 · Certificat',
    title: 'Le certificat CoachDia se mérite.',
    description: 'Module tenu jusqu\'au dernier critere · opposable, partageable, verifiable.',
    type: 'certificate',
    Frame: SceneCertificate,
    voiceover:
      "Ici, un certificat ne se distribue pas automatiquement. Il se gagne. Chaque compétence doit être démontrée jusqu'au dernier critère. Le résultat : une certification crédible, opposable, partageable, et fondée sur des preuves réelles.",
    subtitle: 'Une certification fondée sur des preuves réelles.',
    durationMs: 15900,
    audioPeak: 0.09,
  },
  {
    id: 'transformation',
    chapter: 14,
    kicker: 'Scene 14 · Transformation',
    title: 'Pendant que certains forment encore, d\'autres transforment déjà leurs managers.',
    description: 'CTA final inevitable · urgence concurrentielle.',
    type: 'transformation',
    Frame: SceneTransformation,
    voiceover:
      "Pendant des années, les entreprises ont formé sans vraiment voir ce qui changeait. CoachDia change cela. Les décisions deviennent visibles. Les progrès deviennent démontrables. Les compétences deviennent réelles. Manager après manager. Équipe après équipe. Le leadership cesse enfin d'être théorique. Entrer dans l'expérience CoachDia.",
    subtitle: 'Le leadership devient enfin observable.',
    durationMs: 22600,
    audioPeak: 0.11,
    audioPeakEnd: 0.13,
    cta: {
      primary: { label: 'Démarrer mon parcours', to: '/subscription' },
      secondary: { label: 'Parler à un expert COMEX', to: '/subscription' },
    },
  },
];

export const DEMO_SCENE_COUNT = DEMO_SCENES.length;

// 🕒 Durée totale de la démo (somme des durationMs).
// Source unique consommée par l'audio-gate (`Demo.jsx`) et l'aide à la
// navigation (`navigationHelp.js`) pour rester synchronisés automatiquement
// si une scène est ajoutée/retirée ou si une durée est ajustée.
export const DEMO_TOTAL_DURATION_MS = DEMO_SCENES.reduce(
  (sum, s) => sum + (s.durationMs || 0),
  0,
);
export const DEMO_TOTAL_DURATION_MIN = Math.round(DEMO_TOTAL_DURATION_MS / 60000);
